import React from 'react';
import { ANALYSIS_STEPS } from '@/utils/sse';

interface AnalysisProgressProps {
  currentStatus: string;
  currentMessage?: string;
  error?: string;
}

export const AnalysisProgress: React.FC<AnalysisProgressProps> = ({
  currentStatus,
  currentMessage,
  error
}) => {
  // Find current step index
  const currentStepIndex = ANALYSIS_STEPS.findIndex(step => step.status === currentStatus);
  const isComplete = currentStatus === 'complete';
  const hasError = currentStatus === 'error' || !!error;

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {hasError ? 'Analysis Error' : isComplete ? 'Analysis Complete!' : 'Analyzing Communication...'}
        </h3>
        {!hasError && !isComplete && (
          <div className="animate-spin">
            <span className="material-symbols-outlined text-primary-500 text-2xl">
              progress_activity
            </span>
          </div>
        )}
        {isComplete && (
          <span className="material-symbols-outlined text-green-500 text-2xl">
            check_circle
          </span>
        )}
        {hasError && (
          <span className="material-symbols-outlined text-red-500 text-2xl">
            error
          </span>
        )}
      </div>

      {/* Error Message */}
      {hasError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-800">
            {error || 'An error occurred during analysis. Please try again.'}
          </p>
        </div>
      )}

      {/* Current Status Message */}
      {currentMessage && !hasError && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
          <p className="text-sm text-blue-800">{currentMessage}</p>
        </div>
      )}

      {/* Progress Steps */}
      <div className="space-y-3">
        {ANALYSIS_STEPS.map((step, index) => {
          const isCurrentStep = step.status === currentStatus;
          const isCompleted = index < currentStepIndex || isComplete;
          const isActive = index <= currentStepIndex;

          return (
            <div
              key={step.status}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                isCurrentStep
                  ? 'bg-primary-50 border border-primary-200'
                  : isCompleted
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-gray-50 border border-gray-200'
              }`}
            >
              <div
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  isCompleted
                    ? 'bg-green-500'
                    : isCurrentStep
                    ? 'bg-primary-500'
                    : 'bg-gray-300'
                }`}
              >
                <span className="material-symbols-outlined text-white text-lg">
                  {isCompleted ? 'check' : isCurrentStep ? 'pending' : step.icon}
                </span>
              </div>
              <div className="flex-1">
                <p
                  className={`text-sm font-medium ${
                    isActive ? 'text-gray-900' : 'text-gray-500'
                  }`}
                >
                  {step.label}
                </p>
              </div>
              {isCurrentStep && !isCompleted && (
                <div className="flex-shrink-0">
                  <div className="animate-pulse w-2 h-2 bg-primary-500 rounded-full"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Completion Message */}
      {isComplete && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800 text-center">
            Analysis complete! Redirecting to results...
          </p>
        </div>
      )}
    </div>
  );
};

